import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Button, Card, Grid, CircularProgress } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import Searchbar from "containers/Searchbar";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  startAfter,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { firestore } from "firebaseConfig";
import { Add as PlusIcon } from "@mui/icons-material";
import { updateFirstLetter } from "utils/helper";
import { htmlToText } from "html-to-text";
import Createable from "containers/Select";
import { foodCategory } from "utils/common";
import { parseNutritionData } from "utils/common";
import { getFormattedClientName } from "utils/helper";
import { components } from "react-select";
import { mapRecipeToOption, matchesSearchTerms } from "utils/common";

const pageSize = 6;

const AllRecipes = ({
  uid,
  setRecipeModalOpen,
  handleRecipeCardClick,
  setDietPlan,
  dietPlan,
  handleAddRecipe,
  setMode,
  setSelectedItem,
  activeSchedule,
}) => {
  const [searchVal, setSearchVal] = useState("");
  const [recipes, setRecipes] = useState([]);
  const [allRecipes, setAllRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [showOtherRecipes, setShowOtherRecipes] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [recipeList, setRecipeList] = useState([]);
  const [selectedFoodCategories, setSelectedFoodCategories] = useState([]);
  const [searchRecipe, setSearchRecipe] = useState([]);

  const observer = useRef();

  const recipesCollection = collection(firestore, "recipe");

  const fetchRecipes = async (
    searchTerm = "",
    startAfterDoc = null,
    showAll = showOtherRecipes
  ) => {
    if (loading) return;
    setLoading(true);

    try {
      let q;

      if (activeSchedule && !showAll) {
        q = query(
          recipesCollection,
          where("adminId", "==", uid),
          where("goesWell", "array-contains", activeSchedule),
          orderBy("name"),
          limit(pageSize)
        );
      } else {
        q = query(
          recipesCollection,
          where("adminId", "==", uid),
          orderBy("name"),
          limit(pageSize)
        );
      }

      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      } else {
        // Reset recipes on fresh fetch
        setRecipes([]);
      }

      const querySnapshot = await getDocs(q);

      let newRecipes = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (searchTerm) {
        newRecipes = newRecipes.filter((recipe) =>
          matchesSearchTerms(recipe.name ?? "", searchTerm)
        );
      }

      if (startAfterDoc) {
        setRecipes((prev) => [...prev, ...newRecipes]);
      } else {
        setRecipes(newRecipes);
      }

      setLastDoc(
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null
      );

      setHasMore(querySnapshot.docs.length === pageSize);
    } catch (error) {
      console.error("Error fetching recipes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allRecipes?.length) {
      setRecipeList(allRecipes.map(mapRecipeToOption));
    }
  }, [allRecipes]);

  useEffect(() => {
    if (!uid) return;

    const q = query(
      recipesCollection,
      where("adminId", "==", uid),
      orderBy("name")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const recipesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllRecipes(recipesData);
    });

    return () => unsubscribe();
  }, [uid]);

  useEffect(() => {
    if (!searchRecipe || searchRecipe.length === 0) return;

    setFilteredRecipes((prev) => {
      const newRecipes = searchRecipe.filter(
        (item) => !prev.some((r) => r.id === item.id)
      );

      return [...newRecipes, ...prev];
    });
  }, [searchRecipe]);

  const filterRecipes = useCallback(() => {
    let filtered = recipes;

    if (!showOtherRecipes && activeSchedule) {
      filtered = filtered.filter(
        (recipe) =>
          Array.isArray(recipe.goesWell) &&
          recipe.goesWell.includes(activeSchedule)
      );
    }

    if (selectedFoodCategories.length > 0) {
      const selectedValues = selectedFoodCategories.map((cat) => cat.value);
      filtered = filtered.filter((recipe) =>
        (Array.isArray(recipe.foodCategory) ? recipe.foodCategory : []).some(
          (cat) => selectedValues.includes(cat)
        )
      );
    }

    setFilteredRecipes(filtered);
  }, [recipes, activeSchedule, showOtherRecipes, selectedFoodCategories]);

  const handleSearch = async (value) => {
    setSearchVal(value);
    setLastDoc(null);
    setHasMore(true);
    await fetchRecipes(value, null, showOtherRecipes);
  };

  const lastRecipeElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchRecipes(searchVal, lastDoc);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, lastDoc, hasMore, searchVal, activeSchedule, showOtherRecipes]
  );

  useEffect(() => {
    if (uid) {
      fetchRecipes();
    }
  }, [uid, activeSchedule, showOtherRecipes]);

  useEffect(() => {
    setShowOtherRecipes(false);
    setSearchVal("");
    setLastDoc(null);
    setHasMore(true);
    fetchRecipes("", null, false);
  }, [uid, activeSchedule]);

  useEffect(() => {
    filterRecipes();
  }, [
    recipes,
    activeSchedule,
    showOtherRecipes,
    filterRecipes,
    selectedFoodCategories,
  ]);

  return (
    <Card sx={{ p: 3, mb: 3, borderRadius: "10px", background: "" }}>
      <SuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        pt={1}
        pb={0.8}
        px={1}
        sx={{ backgroundColor: "white" }}
      >
        <SuiTypography
          mb={1}
          variant="h6"
          sx={{
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          Choose recipes to add
        </SuiTypography>

        <SuiBox
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            alignItems: "center",
            flex: 1,
          }}
        >
          <SuiButton
            component={Button}
            color="dark"
            variant="gradient"
            onClick={() => {
              setSelectedItem(null);
              setRecipeModalOpen(true);
              setMode("create");
            }}
            size="small"
            sx={{
              height: "36px",
              minWidth: "180px",
              maxWidth: "180px",
              flex: "1 1 180px",
              px: 2,
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              gap: 1,
              whiteSpace: "nowrap",
              borderRadius: "8px",
              justifyContent: "center",
            }}
          >
            <PlusIcon style={{ fontSize: 16 }} />
            Create new recipe
          </SuiButton>
        </SuiBox>
      </SuiBox>

      <SuiBox
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          justifyContent: "flex-start",
          alignItems: "center",
          flex: 1,
          minWidth: "300px",
          marginBottom: 2,
        }}
      >
        <Createable
          isMulti
          options={foodCategory}
          placeholder="Filter food category"
          value={selectedFoodCategories}
          onChange={(selected) => setSelectedFoodCategories(selected)}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPosition="fixed"
          inputStyle={{
            width: 130,
            height: "1.800rem",
            borderRadius: "0.5rem",
          }}
        />

        <Createable
          isMulti
          options={recipeList ?? []}
          value={searchRecipe}
          filterOption={(option, inputValue) =>
            matchesSearchTerms(option.label ?? "", inputValue)
          }
          style={{ borderRadius: "0.5rem", fontSize: 14 }}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPosition="fixed"
          inputStyle={{
            maxWidth: 130,
            minWidth: 80,
            height: "1.800rem",
            borderRadius: "0.5rem",
          }}
          placeholder="Search recipe name"
          onChange={(selected) => setSearchRecipe(selected)}
          components={{
            MultiValueLabel: (props) => (
              <components.MultiValueLabel {...props}>
                {props.data.label.length > 10
                  ? props.data.label.slice(0, 10) + "..."
                  : props.data.label}
              </components.MultiValueLabel>
            ),
          }}
        />
      </SuiBox>

      {/* Recipe list */}
      {filteredRecipes.length === 0 && !loading ? (
        <Box display="flex" justifyContent="center" my={2}>
          <SuiTypography variant="caption" color="text.secondary">
            No recipes suggested for this schedule
          </SuiTypography>
        </Box>
      ) : (
        <SuiBox
          sx={{ minHeight: 500, maxHeight: 500, overflowY: "auto", p: 0 }}
        ><SuiTypography
              mb={1}
              variant="h6"
              sx={{
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Suggested Recipes
            </SuiTypography>
          <Grid container spacing={2}>
            
            {filteredRecipes.map((recipe, index) => {
              const timeslotCount = (
                dietPlan && typeof dietPlan === "object"
                  ? Object.values(dietPlan).filter(
                      (slot) =>
                        Array.isArray(slot) &&
                        slot.some((r) => r && r.id === recipe.id)
                    )
                  : []
              ).length;

              const isAdded = timeslotCount > 0;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  key={recipe.id}
                  ref={
                    filteredRecipes.length === index + 1
                      ? lastRecipeElementRef
                      : null
                  }
                >
                  <Card
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.2s",
                      px: 1,
                      py: 1,
                      position: "relative",
                      borderRadius: "10px",
                      "&:hover": {
                        boxShadow: 6,
                        transform: "translateY(-1px)",
                      },
                    }}
                    onClick={() => handleRecipeCardClick(recipe)}
                  >
                    {isAdded && (
                      <SuiBox
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "#4caf50",
                          color: "#fff",
                          px: 1,
                          py: 0.2,
                          fontSize: "10px",
                          borderRadius: "4px",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          zIndex: 1,
                        }}
                      >
                        Added ({timeslotCount})
                      </SuiBox>
                    )}

                    <SuiBox
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                      }}
                    >
                      {recipe.image ? (
                        <SuiBox
                          component="img"
                          sx={{
                            objectFit: "cover",
                            borderRadius: 2,
                            width: 100,
                            minWidth: 100,
                            height: 90,
                          }}
                          src={recipe.image}
                          alt={recipe.name}
                        />
                      ) : (
                        <SuiBox
                          sx={{
                            width: 100,
                            height: 90,
                            minWidth: 100,
                            borderRadius: 2,
                            backgroundColor: "#f0f0f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "40px",
                            fontWeight: 700,
                            color: "#888",
                            textTransform: "uppercase",
                          }}
                        >
                          {recipe.name
                            .split(" ")
                            .slice(0, 2)
                            .map((word) => word.charAt(0))
                            .join("")}
                        </SuiBox>
                      )}

                      <SuiBox>
                        <SuiTypography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "14px" }}
                        >
                          {updateFirstLetter(recipe.name)}
                        </SuiTypography>

                        <SuiTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            fontSize: "12px",
                          }}
                        >
                          {htmlToText(recipe.description || "", {
                            wordwrap: false,
                          })}
                        </SuiTypography>
                      </SuiBox>
                    </SuiBox>

                    <SuiBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.5,
                      }}
                    >
                      <SuiTypography
                        variant="body2"
                        color="#ACACAC"
                        sx={{
                          font: "Poppins",
                          fontWeight: 600,
                          fontSize: "14px",
                        }}
                      >
                        🔥 {recipe.calories} Cal
                      </SuiTypography>
                      <SuiButton
                        component={Button}
                        color="dark"
                        variant="gradient"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddRecipe(recipe);
                        }}
                        sx={{
                          px: 2,
                          minWidth: "fit-content",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <PlusIcon style={{ fontSize: 16 }} />
                        Add
                      </SuiButton>
                    </SuiBox>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {loading && (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          )}
          {!hasMore && (
            <Box display="flex" justifyContent="center" my={2}>
              <SuiTypography variant="caption" color="text.secondary">
                All loaded. Add more from search.
              </SuiTypography>
            </Box>
          )}
        </SuiBox>
      )}
    </Card>
  );
};

export default AllRecipes;
